import { useState, useEffect, useCallback } from "react";
import { Loader, Select, SelectOption, SelectProps } from "rizzui";
import ProjectRepository from "@/utilities/repositories/Project";
import { IProject } from "@/utilities/types/Project";
import { debounce } from "lodash";
import cn from "@/utils/class-names";

export interface ProjectsFilterProps
  extends Omit<SelectProps<SelectOption>, "options"> {
  onChange: (selectedProject: IProject | null) => void;
  placeholder?: string;
  className?: string;
  label?: string;
}

export default function ProjectsFilter({
  onChange,
  placeholder = "Select project",
  className,
  label,
  ...props
}: Readonly<ProjectsFilterProps>) {
  const [projects, setProjects] = useState<IProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(
    null
  );

  const [searchQuery, setSearchQuery] = useState("");

  // Transform projects data into Select options format
  const transformProjectsToOptions = (projects: IProject[]): SelectOption[] => {
    return projects.map((project) => ({
      value: project.id,
      label:
        typeof project.name === "string" ? project.name : project.name.en || "",
      data: project,
    }));
  };

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ProjectRepository.getProject({
        queryParams: {
          pagination: {
            limit: 20,
            page: 1,
          },
          query: {
            search: {
              search: searchQuery,
            },
            filter: [],
            sort: {
              createdAt: "DESC",
            },
          },
        },
      });
      if (response.data.items) {
        setProjects(response.data.items);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Initialize projects
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSelectChange = (option: SelectOption | null) => {
    setSelectedProject(option);
    if (option?.data) {
      onChange(option.data as IProject);
    } else {
      onChange(null);
    }
  };

  useEffect(() => {
    if (!props.value) {
      setSelectedProject(null);
      onChange(null);
    }
  }, [props.value]);

  return (
    <div className={cn("w-[250px]", className)}>
      <Select
        label={label}
        searchable
        clearable
        placeholder={placeholder}
        searchPlaceHolder="Search by name"
        searchSuffix={loading ? <Loader size="sm" /> : undefined}
        {...props}
        onSearchChange={debounce(setSearchQuery, 500)}
        disableDefaultFilter
        searchClassName="sticky"
        options={transformProjectsToOptions(projects)}
        value={selectedProject}
        onChange={handleSelectChange}
        onClear={() => {
          setSelectedProject(null);
          onChange(null);
        }}
      />
    </div>
  );
}
